import React, { useState, useEffect } from 'react';
import { X, Save, Power, CircleDot, Droplets, Palette, Check } from 'lucide-react';

function pointToSegmentDistance(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const l2 = dx*dx + dy*dy;
    if (l2 === 0) return Math.sqrt((px-x1)*(px-x1) + (py-y1)*(py-y1));
    let t = ((px - x1) * dx + (py - y1) * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    const closestX = x1 + t * dx;
    const closestY = y1 + t * dy;
    return Math.sqrt((px - closestX)*(px - closestX) + (py - closestY)*(py - closestY));
}

function getDistanceToPipe(valveLat: number, valveLng: number, pipeGeom: any) {
    if (!pipeGeom || pipeGeom.type !== 'LineString') return Infinity;
    const coords = pipeGeom.coordinates; // list of [lng, lat]
    let minD = Infinity;
    for (let i = 0; i < coords.length - 1; i++) {
        const [x1, y1] = coords[i]; // x=lng, y=lat
        const [x2, y2] = coords[i+1];
        const d = pointToSegmentDistance(valveLng, valveLat, x1, y1, x2, y2);
        if (d < minD) minD = d;
    }
    return minD;
}

// 4 predefiniowane żywe kolory dla 4 głównych stacji SUW
export const SUW_PRESET_COLORS = [
    { name: 'Szmaragdowy', hex: '#10b981' },
    { name: 'Fioletowy', hex: '#8b5cf6' },
    { name: 'Cyjanowy', hex: '#06b6d4' },
    { name: 'Bursztynowy', hex: '#f59e0b' }
];

const ValveModal = ({ valve, onClose, onUpdate, onToggle, role, pipes }: any) => {
    const [name, setName] = useState(valve.name || '');
    const [desc, setDesc] = useState(valve.description || '');
    const [lat, setLat] = useState(valve.lat);
    const [lng, setLng] = useState(valve.lng);
    const [customColor, setCustomColor] = useState<string>(valve.custom_color || '#10b981');
    const [selectedPipeIds, setSelectedPipeIds] = useState<number[]>(valve.pipe_ids || []);

    const isSUW = valve.valve_type === 'suw';
    const canEdit = role === 'admin' || role === 'designer' || role === 'superadmin';

    // Synchronizacja stanu przy zmianie aktywnego obiektu (naprawa błędu pamiętania poprzedniego obiektu)
    useEffect(() => {
        setName(valve.name || '');
        setDesc(valve.description || '');
        setLat(valve.lat);
        setLng(valve.lng);
        setCustomColor(valve.custom_color || '#10b981');
        setSelectedPipeIds(valve.pipe_ids || []);
    }, [valve]);

    // Obliczamy rury w pobliżu
    const allPipes = pipes?.features || [];
    const nearbyPipes = allPipes.filter((p: any) => {
        const pid = p.properties.id;
        const isAssociated = selectedPipeIds.includes(pid);
        const dist = getDistanceToPipe(parseFloat(lat), parseFloat(lng), p.geometry);
        return isAssociated || dist < 0.0002; // ok. 20 metrów
    });

    return (
        <div 
            className="valve-modal-container"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <style>{`
                .pipe-checkbox-list {
                    max-height: 90px;
                    overflow-y: auto;
                    border: 1px solid #333;
                    border-radius: 6px;
                    padding: 4px;
                    background-color: #0d0d0d;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .pipe-checkbox-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 5px 8px;
                    border-radius: 5px;
                    background-color: #161616;
                    border: 1px solid #222;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    user-select: none;
                }
                .pipe-checkbox-row:hover {
                    background-color: #222;
                    border-color: #444;
                }
                .pipe-checkbox-row.disabled {
                    cursor: default;
                    opacity: 0.7;
                }
                .pipe-checkbox-row.disabled:hover {
                    background-color: #161616;
                    border-color: #222;
                }
                .pipe-checkbox-row input[type="checkbox"] {
                    width: 15px;
                    height: 15px;
                    accent-color: #2563eb;
                    cursor: pointer;
                    margin: 0;
                }
                .pipe-checkbox-row.disabled input[type="checkbox"] {
                    cursor: default;
                }
                .pipe-checkbox-label {
                    font-size: 12px;
                    font-weight: 500;
                    flex: 1;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    min-width: 0;
                }
                .pipe-name-text {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    padding-right: 6px;
                }
                .pipe-status-tag {
                    font-size: 9px;
                    font-weight: bold;
                    padding: 1px 5px;
                    border-radius: 4px;
                    flex-shrink: 0;
                }
                .pipe-status-tag.open {
                    background-color: #22c55e22;
                    color: #22c55e;
                    border: 1px solid #22c55e44;
                }
                .pipe-status-tag.closed {
                    background-color: #ef444422;
                    color: #ef4444;
                    border: 1px solid #ef444444;
                }
                .suw-color-swatch {
                    width: 28px;
                    height: 28px;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.15s ease, border-color 0.15s ease;
                    border: 2px solid transparent;
                }
                .suw-color-swatch:hover {
                    transform: scale(1.1);
                }
                .suw-color-swatch.active {
                    border-color: white;
                    box-shadow: 0 0 10px rgba(255,255,255,0.4);
                }
            `}</style>

            {/* Nagłówek okna */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isSUW ? (
                        <Droplets size={18} color={valve.is_closed ? '#ef4444' : customColor} />
                    ) : (
                        <CircleDot size={16} color={valve.is_closed ? '#ef4444' : '#22c55e'} />
                    )}
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
                        {name || (isSUW ? `Stacja SUW #${valve.id}` : `Zawór #${valve.id}`)}
                    </h3>
                </div>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onClose();
                    }}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        borderRadius: '6px'
                    }}
                    title="Zamknij okno"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Plakietki statusu */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                <span style={{
                    padding: '3px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: 'bold',
                    backgroundColor: isSUW ? `${customColor}22` : '#3b82f622',
                    color: isSUW ? customColor : '#60a5fa',
                    border: `1px solid ${isSUW ? customColor : '#3b82f6'}`
                }}>
                    {isSUW ? 'STACJA UZDATNIANIA WODY' : 'ZASUWA SIECIOWA'}
                </span>

                <span style={{
                    padding: '3px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: 'bold',
                    backgroundColor: valve.is_closed ? '#ef444433' : '#22c55e33',
                    color: valve.is_closed ? '#ef4444' : '#22c55e',
                    border: `1px solid ${valve.is_closed ? '#ef4444' : '#22c55e'}`
                }}>
                    {valve.is_closed ? 'ZAMKNIĘTY' : 'PRACUJE / OTWARTY'}
                </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Kolor SUW i dziedziczenie dla rur */}
                {isSUW && (
                    <div style={{ backgroundColor: '#111', padding: '8px 10px', borderRadius: '8px', border: '1px solid #222' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <label style={{ fontSize: '11px', color: '#aaa', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Palette size={13} color={customColor} /> Kolor magistrali
                            </label>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                                Rury przejmą ten kolor
                            </div>
                        </div>

                        {/* Paleta 4 presetów + picker */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {SUW_PRESET_COLORS.map(preset => (
                                <div
                                    key={preset.hex}
                                    title={preset.name}
                                    className={`suw-color-swatch ${customColor.toLowerCase() === preset.hex.toLowerCase() ? 'active' : ''}`}
                                    style={{ backgroundColor: preset.hex }}
                                    onClick={() => canEdit && setCustomColor(preset.hex)}
                                >
                                    {customColor.toLowerCase() === preset.hex.toLowerCase() && (
                                        <Check size={14} color="white" strokeWidth={3} />
                                    )}
                                </div>
                            ))}

                            {/* Własny selektor HEX */}
                            {canEdit && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                                    <input
                                        type="color"
                                        value={customColor}
                                        onChange={e => setCustomColor(e.target.value)}
                                        style={{ width: '24px', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'transparent' }}
                                    />
                                    <span style={{ fontSize: '10px', color: '#aaa', fontFamily: 'monospace' }}>{customColor}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Nazwa */}
                <div>
                    <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '3px' }}>
                        {isSUW ? 'Nazwa Stacji Uzdatniania Wody' : 'Nazwa / Numer zasuwy'}
                    </label>
                    <input
                        disabled={!canEdit}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder={isSUW ? 'np. SUW Gruta' : 'np. Z-101'}
                        style={{
                            width: '100%', backgroundColor: '#000', border: '1px solid #333',
                            color: 'white', padding: '7px 10px', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px'
                        }}
                    />
                </div>

                {/* Opis */}
                <div>
                    <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '3px' }}>Opis / Uwagi techniczne</label>
                    <textarea
                        disabled={!canEdit}
                        value={desc}
                        onChange={e => setDesc(e.target.value)}
                        rows={1}
                        placeholder={isSUW ? 'Wydajność, uwagi...' : 'Uwagi techniczne...'}
                        style={{
                            width: '100%', backgroundColor: '#000', border: '1px solid #333',
                            color: 'white', padding: '7px 10px', borderRadius: '6px', boxSizing: 'border-box', resize: 'vertical', minHeight: '34px', fontSize: '13px'
                        }}
                    />
                </div>

                {/* Współrzędne (admin) */}
                {(role === 'admin' || role === 'superadmin') && (
                    <div>
                        <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '3px' }}>Współrzędne GPS</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input type="number" value={lat} onChange={e => setLat(e.target.value)} style={{ width: '50%', backgroundColor: '#000', border: '1px solid #333', color: 'white', padding: '6px 8px', borderRadius: '6px', fontSize: '12px' }} />
                            <input type="number" value={lng} onChange={e => setLng(e.target.value)} style={{ width: '50%', backgroundColor: '#000', border: '1px solid #333', color: 'white', padding: '6px 8px', borderRadius: '6px', fontSize: '12px' }} />
                        </div>
                    </div>
                )}

                {/* Obsługiwane rury / magistrale */}
                <div>
                    <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '3px' }}>
                        {isSUW ? 'Wychodzące magistrale i rury' : 'Obsługiwane rury'}
                    </label>
                    {nearbyPipes.length === 0 ? (
                        <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', padding: '6px 8px', backgroundColor: '#0d0d0d', borderRadius: '6px' }}>
                            Brak rur w bezpośrednim sąsiedztwie
                        </div>
                    ) : (
                        <div className="pipe-checkbox-list">
                            {nearbyPipes.map((p: any) => {
                                const pid = p.properties.id;
                                const isChecked = selectedPipeIds.includes(pid);
                                
                                const nameRaw = p.properties.name;
                                const labelName = (nameRaw && nameRaw.trim() !== '') ? nameRaw.trim() : `Rura #${pid}`;
                                const isOpen = p.properties.status === 'open';

                                return (
                                    <label 
                                        key={pid} 
                                        className={`pipe-checkbox-row ${!canEdit ? 'disabled' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            disabled={!canEdit}
                                            checked={isChecked}
                                            onChange={() => {
                                                if (isChecked) {
                                                    setSelectedPipeIds(selectedPipeIds.filter(id => id !== pid));
                                                } else {
                                                    setSelectedPipeIds([...selectedPipeIds, pid]);
                                                }
                                            }}
                                        />
                                        <div className="pipe-checkbox-label">
                                            <span className="pipe-name-text" style={{ color: isOpen ? (isSUW ? customColor : '#93c5fd') : '#fca5a5' }}>
                                                {labelName}
                                            </span>
                                            <span className={`pipe-status-tag ${isOpen ? 'open' : 'closed'}`}>
                                                {isOpen ? 'OTW' : 'ZAMK'}
                                            </span>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Przyciski akcji */}
            <div style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onToggle(valve.id);
                        onClose();
                    }}
                    style={{
                        flex: 1, padding: '9px 10px', borderRadius: '8px',
                        backgroundColor: valve.is_closed ? '#166534' : '#991b1b',
                        color: 'white', border: 'none', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', gap: '6px', fontWeight: 600,
                        fontSize: '13px', transition: 'background-color 0.2s ease'
                    }}
                >
                    <Power size={15} /> {valve.is_closed ? (isSUW ? 'Uruchom' : 'Otwórz') : (isSUW ? 'Wyłącz' : 'Zamknij')}
                </button>

                {canEdit && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onUpdate(valve.id, {
                                name,
                                description: desc,
                                custom_color: isSUW ? customColor : undefined,
                                lat: parseFloat(lat),
                                lng: parseFloat(lng),
                                pipe_ids: selectedPipeIds
                            });
                            onClose();
                        }}
                        style={{
                            flex: 1, padding: '9px 10px', borderRadius: '8px', backgroundColor: '#2563eb',
                            color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold',
                            fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                        }}
                    >
                        <Save size={15} /> Zapisz
                    </button>
                )}
            </div>
        </div>
    );
};

export default ValveModal;